import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { MemberPickerBottomSheetProvider } from '@euler/app/components/MemberPickerBottomSheet';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { PlateNoScreen } from '@euler/app/flows/common/PlateNoScreen';
import { QrcodeScanScreen } from '@euler/app/flows/order/pages/QrcodeScanScreen';
import { Aside, AsideRef } from '@euler/components/Aside';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { FontFamily } from '@euler/components/typography';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  VehicleInspectionTask,
  VehicleReceptionOrder,
} from '@euler/model/entity';
import { SiteInspectionStaged, TaskBasicInfo } from '@euler/model/task-detail';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { getDefaultHeaderHeight } from '@react-navigation/elements';
import { useNavigation } from '@react-navigation/native';
import {
  CardStyleInterpolators,
  StackNavigationProp,
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import { StackHeaderMode } from '@react-navigation/stack/lib/typescript/src/types';
import * as Sentry from '@sentry/react';
import Constants from 'expo-constants';
import { StatusBar } from 'expo-status-bar';
import React, { memo, useCallback, useRef } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';
import { css, useTheme } from 'styled-components';
import { FacadeInspectionScreen } from './/pages/FacadeInspectionScreen';
import { OrderFlowContextProvider } from './OrderFlowContext';
import { AddVehicleInfoScreen } from './pages/AddVehicleInfoScreen';
import { DashboardInspectionScreen } from './pages/DashboardInspectionScreen';
import { OrderPreviewScreen } from './pages/OrderPreviewScreen';
import { OrderScreen } from './pages/OrderScreen';
import { ServiceAgentSelectorScreen } from './pages/ServiceAgentSelectorScreen';
import { ServiceSubjectsScreen } from './pages/ServiceSubjectsScreen';
import { VehicleBrandSelectorScreen } from './pages/VehicleBrandSelectorScreen';
import { VehicleModelSelectorScreen } from './pages/VehicleModelSelectorScreen';
import {
  OrderForm,
  OrderFormMode,
  OrderFormType,
  OrderPageCapabilities,
} from './types';

const Stack = createStackNavigator();

export const OrderScreens = {
  _orderPage: OrderScreen,
  _plate: PlateNoScreen,
  _serviceAgentSelector: ServiceAgentSelectorScreen,
  _vehicleBrandSelector: VehicleBrandSelectorScreen,
  _vehicleModelSelector: VehicleModelSelectorScreen,
  _addVehicleInfo: AddVehicleInfoScreen,
  _dashboardInspection: DashboardInspectionScreen,
  _facadeInspection: FacadeInspectionScreen,
  _serviceSubjects: ServiceSubjectsScreen,
  _previewOrder: OrderPreviewScreen,
};

const ModalScreens = {
  _qrcodeScan: QrcodeScanScreen,
};

export type OrderNavParams = ScreenToParams<typeof OrderScreens> &
  ScreenToParams<typeof ModalScreens>;

type Props = {
  mode: OrderFormMode;
  type: OrderFormType;
  basicInfo?: TaskBasicInfo;
  defaultValues?: Partial<OrderForm>;
  preInspection?: SiteInspectionStaged[];
  capabilities?: OrderPageCapabilities;
  onCreated?: (
    order: VehicleReceptionOrder,
    task: VehicleInspectionTask,
  ) => void;
  onUpdated?: (order: VehicleReceptionOrder) => void;
  onNavigateToExistingTask?: (taskNo: string) => void;
} & ({ type: 'child'; mainOrderNo: string } | { type: 'main' });

export const OrderFlow = wrapNavigatorScreen(
  (props: Props) => {
    const isMobile = useIsMobileLayout();
    const { width } = useWindowDimensions();
    const { rpx } = useResponsiveContext();
    const { bottom, top } = useSafeAreaInsets();
    const navigation = useNavigation<StackNavigationProp<any>>();
    const asideRef = useRef<AsideRef>(null);
    const isAsideDismissedAsFinish = useRef(false);
    const finishCallback = useRef<() => void>();
    const headerMode$ = useCreation(
      () => new BehaviorSubject<StackHeaderMode>('float'),
      [],
    );

    // this function is only expected to be called in nested stack screen
    // in the order flow navigation stack after everything is cleaned up.
    const finish = usePersistFn((callback?: () => void) => {
      if (asideRef.current) {
        // for aside, it's a two-step finish routine, first, we have to
        // dismiss the aside in an animated way, then when the animation
        // finishes, we remove the entire navigation stack and go back
        // to the main screen. So on finish is called, we first initiate
        // the animated dismiss of the aside.
        isAsideDismissedAsFinish.current = true;
        asideRef.current.dismiss();
        finishCallback.current = callback;
      } else {
        // for normal screen, we simply remove the entire navigation stack
        navigation.goBack();
        callback?.();
      }
    });

    const onAsideDismissed = useCallback(() => {
      if (isAsideDismissedAsFinish.current) {
        navigation.goBack();
        finishCallback.current?.();
        finishCallback.current = undefined;
        isAsideDismissedAsFinish.current = false;
        return true;
      }
      return false;
    }, [navigation]);

    const title =
      props.mode === 'update'
        ? '修改订单信息'
        : props.type === 'main'
        ? '车辆建单'
        : '新建子订单';

    if (isMobile) {
      return (
        <AppNavigationProvider>
          <SafeAreaInsetsContext.Provider
            value={{
              left: 0,
              right: 0,
              top: Platform.select({ ios: top, default: top }),
              bottom,
            }}
          >
            <MemberPickerBottomSheetProvider>
              <OrderFlowContextProvider
                finish={finish}
                headerMode$={headerMode$}
                {...props}
              >
                <Content title={title} headerMode$={headerMode$} />
              </OrderFlowContextProvider>
            </MemberPickerBottomSheetProvider>
          </SafeAreaInsetsContext.Provider>
        </AppNavigationProvider>
      );
    }
    return (
      <AppNavigationProvider>
        <Aside
          ref={asideRef}
          dismissible={false}
          disableDefaultNavigationHandler={true}
          width={Math.max(rpx(500), width * 0.45)}
          withoutKeyboardAvoidingView
          onDismissed={onAsideDismissed}
          css={`
            padding-top: ${0}px;
          `}
          backdropCss={css`
            background-color: rgba(0, 0, 0, 0.7);
          `}
        >
          <MemberPickerBottomSheetProvider>
            <OrderFlowContextProvider
              finish={finish}
              headerMode$={headerMode$}
              {...props}
            >
              <Content title={title} headerMode$={headerMode$} />
            </OrderFlowContextProvider>
          </MemberPickerBottomSheetProvider>
        </Aside>
      </AppNavigationProvider>
    );
  },
  adaptiveWrapperOptions({
    cardStyleInterpolator: CardStyleInterpolators.forScaleFromCenterAndroid,
  }),
);

const Content = memo(
  ({
    headerMode$,
    ...props
  }: {
    title: string;
    headerMode$: BehaviorSubject<StackHeaderMode>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const isMobile = useIsMobileLayout();
    const dimens = useWindowDimensions();
    const statusBarHeight = Constants.statusBarHeight;
    const headerHeight = getDefaultHeaderHeight(dimens, false, statusBarHeight);
    const [headerMode] = useBehaviorSubject(headerMode$);
    const tabletStyles = isMobile
      ? undefined
      : {
          headerStyle: {
            height: headerHeight,
          },
          headerLeftContainerStyle: {
            paddingTop: statusBarHeight,
          },
          headerRightContainerStyle: {
            paddingTop: statusBarHeight,
          },
          headerTitleContainerStyle: {
            paddingTop: statusBarHeight,
          },
        };
    return (
      <Sentry.ErrorBoundary
        // @ts-ignore
        fallback={(params: any) => <ErrorFallback {...params} />}
      >
        <StatusBar style={'dark'} />
        <Stack.Navigator initialRouteName={'_orderPage'}>
          <Stack.Group
            screenOptions={{
              ...TransitionPresets.SlideFromRightIOS,
              headerMode: headerMode,
              headerTitleAlign: 'center',
              headerBackTitle: ' ',
              headerTitleStyle: {
                fontSize: rpx(18),
                fontFamily: FontFamily.NotoSans.Bold,
              },
              cardStyle: { backgroundColor: theme.page.card.backgroundColor },
              headerTitle: MultilineHeaderTitle,
              headerBackImage: () => <BackIcon />,
              ...tabletStyles,
            }}
          >
            {spreadScreens(Stack.Screen, OrderScreens, '_orderPage', props)}
          </Stack.Group>
          <Stack.Group
            screenOptions={{
              presentation: 'transparentModal',
              headerShown: false,
              animationEnabled: false,
              cardStyle: { backgroundColor: 'transparent' },
            }}
          >
            {spreadScreens(Stack.Screen, ModalScreens)}
          </Stack.Group>
        </Stack.Navigator>
      </Sentry.ErrorBoundary>
    );
  },
);
